/**
 * Lightweight XSS-safe markdown renderer for Shadow DOM.
 * Supports: paragraphs, bold, italic, links, inline code, code fences,
 * headings, unordered/ordered lists, line breaks.
 *
 * Links are extracted BEFORE underscore/asterisk emphasis so URLs that
 * contain `_` (e.g. LinkedIn posts) are never corrupted into <em> tags.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function sanitizeUrl(url: string): string {
  const trimmed = (url || "").trim();
  if (!trimmed) return "#";
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith("javascript:") ||
    lower.startsWith("data:") ||
    lower.startsWith("vbscript:")
  ) {
    return "#";
  }
  // Reject obvious truncation / whitespace corruption
  if (/\s/.test(trimmed)) return "#";
  return trimmed;
}

function linkHtml(label: string, href: string): string {
  const safeHref = sanitizeUrl(href);
  const isMailto = safeHref.toLowerCase().startsWith("mailto:");
  const targetAttrs = isMailto
    ? ""
    : ` target="_blank" rel="noopener noreferrer"`;
  return `<a href="${escapeHtml(safeHref)}"${targetAttrs}>${escapeHtml(label)}</a>`;
}

function inlineMarkdown(text: string): string {
  const links: string[] = [];

  // Protect markdown links first (href may contain underscores).
  // Allow http(s) and mailto so follow-up emails render as clickable links.
  let protectedText = text.replace(
    /\[([^\]]*)\]\(((?:https?:\/\/|mailto:)[^)\s]+)\)/gi,
    (_m, label: string, href: string) => {
      const i = links.length;
      links.push(linkHtml(label, href));
      return `@@LINK${i}@@`;
    }
  );

  let out = escapeHtml(protectedText);

  // inline code
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");

  // bold (**...** only — avoid __ which conflicts with URL underscores)
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

  // italic (*...* only — underscore italics disabled to protect URLs/ids)
  out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");

  // Restore protected links (placeholders were escaped as-is @@LINK0@@)
  out = out.replace(/@@LINK(\d+)@@/g, (_m, idx: string) => {
    return links[Number(idx)] || "";
  });

  return out;
}

function preprocess(text: string): string {
  let cleaned = text.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n");
  const ticks = (cleaned.match(/```/g) || []).length;
  if (ticks % 2 !== 0) cleaned += "\n```";
  const bold = (cleaned.match(/\*\*/g) || []).length;
  if (bold % 2 !== 0) cleaned += "**";
  return cleaned;
}

export function renderMarkdown(raw: string): string {
  if (!raw) return "";
  const text = preprocess(raw);
  const lines = text.split("\n");
  const html: string[] = [];
  let inCode = false;
  let codeBuf: string[] = [];
  let inUl = false;
  let inOl = false;
  let para: string[] = [];

  const closeLists = () => {
    if (inUl) {
      html.push("</ul>");
      inUl = false;
    }
    if (inOl) {
      html.push("</ol>");
      inOl = false;
    }
  };

  const flushPara = () => {
    if (para.length) {
      html.push(`<p>${inlineMarkdown(para.join(" "))}</p>`);
      para = [];
    }
  };

  for (const line of lines) {
    if (line.trim().startsWith("```")) {
      flushPara();
      closeLists();
      if (inCode) {
        html.push(`<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`);
        codeBuf = [];
        inCode = false;
      } else {
        inCode = true;
      }
      continue;
    }

    if (inCode) {
      codeBuf.push(line);
      continue;
    }

    const heading = /^(#{1,4})\s+(.+)$/.exec(line);
    if (heading) {
      flushPara();
      closeLists();
      const level = heading[1].length;
      html.push(`<h${level}>${inlineMarkdown(heading[2])}</h${level}>`);
      continue;
    }

    const ul = /^[-*+]\s+(.+)$/.exec(line);
    if (ul) {
      flushPara();
      if (inOl) {
        html.push("</ol>");
        inOl = false;
      }
      if (!inUl) {
        html.push("<ul>");
        inUl = true;
      }
      html.push(`<li>${inlineMarkdown(ul[1])}</li>`);
      continue;
    }

    const ol = /^\d+\.\s+(.+)$/.exec(line);
    if (ol) {
      flushPara();
      if (inUl) {
        html.push("</ul>");
        inUl = false;
      }
      if (!inOl) {
        html.push("<ol>");
        inOl = true;
      }
      html.push(`<li>${inlineMarkdown(ol[1])}</li>`);
      continue;
    }

    if (!line.trim()) {
      flushPara();
      closeLists();
      continue;
    }

    closeLists();
    para.push(line.trim());
  }

  if (inCode) {
    html.push(`<pre><code>${escapeHtml(codeBuf.join("\n"))}</code></pre>`);
  }
  flushPara();
  closeLists();
  return html.join("");
}
